import { getPushPublicKey, subscribeToPush, unsubscribeFromPush } from "@/lib/api/push";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

/**
 * Nutzt getRegistration() statt navigator.serviceWorker.ready — im
 * Vite-Dev-Server wird nie ein Worker registriert (siehe
 * registerServiceWorker.ts), .ready würde also endlos hängen.
 */
async function getActiveRegistration(): Promise<ServiceWorkerRegistration> {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push-Benachrichtigungen werden von diesem Browser nicht unterstützt.");
  }
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    throw new Error("Push-Benachrichtigungen sind nur in der installierten Anwendung verfügbar (nicht in der Entwicklungsvorschau).");
  }
  return registration;
}

export async function getPushSubscriptionState(): Promise<boolean> {
  if (!("serviceWorker" in navigator)) return false;
  const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (!registration) return false;
  const subscription = await registration.pushManager.getSubscription().catch(() => null);
  return Boolean(subscription);
}

export async function enablePush(): Promise<void> {
  const registration = await getActiveRegistration();

  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Die Berechtigung für Benachrichtigungen wurde nicht erteilt.");

  const { publicKey } = await getPushPublicKey();
  if (!publicKey) throw new Error("Push-Benachrichtigungen sind auf dem Server nicht konfiguriert.");

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource
  });
  await subscribeToPush(subscription.toJSON() as PushSubscriptionJSON);
}

export async function disablePush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined);
  if (!registration) return;
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;
  await unsubscribeFromPush(subscription.endpoint);
  await subscription.unsubscribe();
}
